import { Component, OnInit,
  ChangeDetectorRef, EventEmitter,
  Output, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Utilities } from '@shared/models/utilities/utilities.class';
import { distinctUntilChanged, debounceTime } from 'rxjs';
import { TranslateService } from '@shared/services/translate/translate.service';
import { ShopboxDatepicker } from '@shared/components/shopbox-datepicker/src/datepicker/datepicker';
import { StorageService } from '@shared/services';
import { GraphDataFilters } from '@shared/models/graph/graph-filters.class';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { NgClass } from '@angular/common';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { ShopboxDatepickerInput } from '../../../../shared/components/shopbox-datepicker/src/datepicker/datepicker-input';
import { ShopboxDatepickerToggle } from '../../../../shared/components/shopbox-datepicker/src/datepicker/datepicker-toggle';
import { ShopboxDatepicker as ShopboxDatepicker_1 } from '../../../../shared/components/shopbox-datepicker/src/datepicker/datepicker';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslatePipe } from '../../../../shared/pipes/translate.pipe';


@Component({
    selector: 'overview-toolbar',
    templateUrl: './overview-toolbar.component.html',
    styleUrls: ['./overview-toolbar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatToolbar, MatToolbarRow, FormsModule, ReactiveFormsModule, MatFormField, MatSelect, MatOption, MatIcon, MatLabel, MatInput, ShopboxDatepickerInput, NgClass, ShopboxDatepickerToggle, MatSuffix, ShopboxDatepicker_1, MatSlideToggle, MatIconButton, MatTooltip, MatButton, TranslatePipe]
})
export class OverviewToolbarComponent implements OnInit {
  translate: TranslateService;
  @ViewChild('resultPicker', { static: false}) resultPicker: ShopboxDatepicker<Date>;

  @Output() updateEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() changesEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Output() downloadEmitter: EventEmitter<any> = new EventEmitter<any>();
  @Input() preSelectedDate: GraphDataFilters = GraphDataFilters.Today;

  @Input() set activeProgress(val: boolean) {
    this._activeProgress = val;
    this._changeDetector.markForCheck();
  };

  @Input()
  set clientList(value: Array<any>) {
    this._clientList = value;
    this._changeDetector.markForCheck();
    if (value.length === 1) {
      if (this.client) {
        this.client.enable();
        this._changeDetector.markForCheck();
        this.client.setValue(value[0].uid, {emitEvent: false});
        this.client.disable();
        this._changeDetector.markForCheck();
      }
    } else {
      if (this.client) {
        this.client.enable();
        this.client.setValue('null', { emitEvent: false });
        this._changeDetector.markForCheck();
      }
    }
  }

  get clientList(): Array<any> {
    return this._clientList;
  }

  @Input()
  set storeList(value: Array<any>) {
    this._storeList = value;
    this._changeDetector.markForCheck();
    if (value.length === 1) {
      if (this.store) {
        this.store.enable();
        this._changeDetector.markForCheck();
        this.store.setValue(value[0].uid, {emitEvent: false});
        this.store.disable();
        this._changeDetector.markForCheck();
      }
    } else {
      if (this.store) {
        this.store.enable();
        this.store.setValue('null', { emitEvent: false });
        this._changeDetector.markForCheck();
      }
    }
  }

  get storeList(): Array<any> {
    return this._storeList;
  }

  @Input() staffMembersSelector = false;
  @Input() compareRevenue = false;
  @Input() vatNumbers = false;
  @Input() compareRevenueOn = false;
  @Input() compareRevenueText = '';
  @Input() vatOn = false;

  @Input() set clientsSelector (val: boolean) {
    this._clientsSelector = val;
    if (!this._clientsSelector) {
      if (this.client) {
        this.client.setValue(null, {emitEvent: false});
      }
    }
  }
  @Input() set storeSelector (val: boolean) {
    this._storeSelector = val;
    if (!this._storeSelector) {
      if (this.store) {
        this.store.setValue(null, {emitEvent: false});
      }
    }
  }
  @Input() downloadButton = false;
  @Input() updateButton = false;
  @Input() filterControls = false;
  @Input() disableStaffMembersSelector = false;
  f: UntypedFormGroup;
  store: UntypedFormControl;
  client: UntypedFormControl;
  datePicker: UntypedFormControl;
  staffMembers: UntypedFormControl;
  showChildren: UntypedFormControl;

  touch = new Utilities().isMobileDevice();
  optionControl = false;
  maxDate: Date = new Date();

  private previousStore: any;
  _storeList: Array<any> = [];
  _clientList: Array<any> = [];
  _activeProgress: boolean = false;
  _clientsSelector: boolean = false;
  _storeSelector: boolean = false;

  currentClient: any;

  @Output() compareRevenueOnChanged: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() vatOnChanged: EventEmitter<boolean> = new EventEmitter<boolean>();

  constructor(
    public _translate: TranslateService,
    private _changeDetector: ChangeDetectorRef,
    private _storage: StorageService
  ) {
    this.translate = _translate;
  }

  reset() {
    if (this.f) {
      this.f.reset({ emitEvent: false });
    }
  }

  ngOnInit() {
    this.refreshTranslation();
    this.f = this.initForm();
    this.f.valueChanges
      .pipe( debounceTime(200), distinctUntilChanged() )
      .subscribe(() => {
        this.emitChanges();
      });
  }

  refreshTranslation() {
    this.translate.onLangChanged.subscribe(() => {
      this._changeDetector.markForCheck();
    });
  }

  initForm(): UntypedFormGroup {
    this.store = new UntypedFormControl();
    this.datePicker = new UntypedFormControl();
    this.client = new UntypedFormControl();
    this.showChildren = new UntypedFormControl();
    this.staffMembers = new UntypedFormControl(this.staffMembersSelector ? '1' : null);
    this.currentClient = this._storage.getCurrentClient();

    if (this.disableStaffMembersSelector) {
      this.staffMembers.disable({ emitEvent: true});
    } else {
      this.staffMembers.enable({ emitEvent: true});
    }

    if(this.client.value === null){
      this.showChildren.setValue(1);
    } else if(this.client.value != null) {
      this.showChildren.setValue(0);
    }

    const group = new UntypedFormGroup({
      store: this.store,
      datePicker: this.datePicker,
      staffMembers: this.staffMembers,
      client: this.client,
      // showChild: this.showChildren
    });

    return group;
  }

  emitChanges() {
    if (!this.optionControl) {
      if (!this.resultPicker._beginDate || !this.resultPicker._endDate) {
        this.preSelectedDateRange();
      } else {
        const model = this.f.getRawValue();
        this.changesEmitter.emit(model);
      }
    }
    this._changeDetector.markForCheck();
  }

  preSelectedDateRange() {
    switch (this.preSelectedDate) {
      case GraphDataFilters.Today:
        this.resultPicker._selectTodayDate();
        break;
      case GraphDataFilters.Yesterday:
        this.resultPicker._selectYesterdayDate();
        break;
      case GraphDataFilters.This_week:
        this.resultPicker._selectThisWeekDate();
        break;
      case GraphDataFilters.Last_week:
        this.resultPicker._selectLastWeekDate();
        break;
      case GraphDataFilters.This_month:
        this.resultPicker._selectThisMonthDate();
        break;
      case GraphDataFilters.Last_month:
        this.resultPicker._selectLastMonthDate();
        break;
      case GraphDataFilters.This_year:
        this.resultPicker._selectThisYearDate();
        break;
      case GraphDataFilters.Last_year:
        this.resultPicker._selectLastYearDate();
        break;
      default:
        this.resultPicker._selectTodayDate();
        break;
    }
  }

  trackItemFn(index: number, _item: any) {
    return index;
  }

  updateData() {
    this.updateEmitter.emit();
    this._changeDetector.markForCheck();
  }

  toggleOptionControl() {
    if (this.optionControl) {
      this.store.setValue(this.previousStore, {
        emitEvent: false
      });
      this.optionControl = false;
    } else {
      this.previousStore = this.store.value;
      this.optionControl = true;
    }
    this._changeDetector.markForCheck();
  }

  filter() {
    this.optionControl = false;
    this.emitChanges();
  }

  getSelectedValues(): string {
    let store = '';
    let dateAsString = '';
    if (this.store && this.store.value) {
      const index = this._storeList.findIndex(
        element => String(element.uid) === String(this.store.value)
      );
      if (index !== -1) {
        store = this._storeList[index].name;
      } else {
        store = this.translate.instant('ALL_STORES');
      }
    } else {
      store = this.translate.instant('ALL_STORES');
    }
    if (this.datePicker.value && (this.datePicker.value.end && this.datePicker.value.begin)) {
      const begin = new Utilities().formatDateReflact(
        this.datePicker.value.begin.toString(),
        '.',
        true,
        false
      );
      const end = new Utilities().formatDateReflact(
        this.datePicker.value.end.toString(),
        '.',
        true,
        false
      );
      if (begin === end) {
        dateAsString = begin;
      } else {
        dateAsString = begin + ' - ' + end;
      }
    }
    return store + ' / ' + dateAsString;
  }

  donwloadClick() {
    this.downloadEmitter.emit();
    this._changeDetector.markForCheck();
  }

  parent(client: any): boolean {
    return client.uid === this.currentClient.uid;
  }

  selectedClient(client: any): boolean {
    return client.uid !== this.client.value;
  }

  setCompareOnValue() {
    this.compareRevenueOn = !this.compareRevenueOn;
    this.compareRevenueOnChanged.emit(this.compareRevenueOn);
  }

  setVatOnValue() {
    this.vatOn = !this.vatOn;
    this.vatOnChanged.emit(this.vatOn);
  }
}
